import React, { useState, useEffect, useRef, useMemo } from 'react';

import styles from './AddMultipleEbooks.module.css';
import useAdminProfileStore from '../../../store/admin/adminProfileStore';
import { useCategories } from '../../../hooks/categories/useCategories';
import { useSeriesByType } from '../../../hooks/useSeriesData';
import { useMultipleContent } from '../../../hooks/admin/useAdminMultipleContents';
import { useAdminProfile } from '../../../hooks/admin/useAdminProfile';
import JoditEditor from 'jodit-react';


const AddMultipleEbooks = () => {
  const { profile:dataProfile } = useAdminProfile();
  const profile = useAdminProfileStore((state) => state.profile);
  
  // Create refs for file inputs
  const mobileImageInputRef = useRef(null);
  const computerImageInputRef = useRef(null);
  
  const { categories = [] } = useCategories('Ebook');
  const { series = [] } = useSeriesByType('Ebook');
  const { 
    uploadBatch, 
    isUploading, 
    uploadError,
    content: uploadedEbooks,
    refetch: refetchEbooks
  } = useMultipleContent('ebook');

  const [formData, setFormData] = useState({
    genre_id: '',
    language: '',
    genreName: '',  // Added genreName field
    author: '',
    is_public: false,
    have_series: false,
    series_id: '',
    author_id: dataProfile?.id || '',
    format_type: 'ebook',
  });

  const [mobileImage, setMobileImage] = useState(null);
  const [mobileImagePreview, setMobileImagePreview] = useState(null);
  const [computerImage, setComputerImage] = useState(null);
  const [computerImagePreview, setComputerImagePreview] = useState(null);
  const [selectedSeriesImage, setSelectedSeriesImage] = useState(null);
  
  const [ebookChapters, setEbookChapters] = useState([
    {
      title: '',
      slug: '',
      description: '',
      full_content: '',
      chapter_number: 1
    }
  ]);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (profile?.id) {
      setFormData(prev => ({ ...prev, author_id: profile.id }));
    }
  }, [profile]);

  useEffect(() => {
    if (uploadError) {
      setMessage({ text: uploadError, type: 'error' });
    }
  }, [uploadError]);

  

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedValue = type === 'checkbox' ? checked : value;
  
    setFormData({
      ...formData,
      [name]: updatedValue,
    });
  
    // Reset series_id if have_series is set to false
    if (name === 'have_series' && !checked) {
      setFormData(prev => ({ ...prev, series_id: '' }));
      setSelectedSeriesImage(null);
    }
  
    // Set image when a series is selected
    if (name === 'series_id') {
      const selected = series.find(item => item.id === value);
      setSelectedSeriesImage(selected?.coverImageUrl || null);
    }

    if (name === 'genre_id' && value) {
      const selectedCategory = categories.find(category => category.id === value);
      if (selectedCategory) {
        setFormData(prev => ({ ...prev, genre_name: selectedCategory.name }));
      }
    }
  };
const editor = useRef(null);

const joditConfig = useMemo(() => ({
  theme: 'dark',
  height: 600,
  enableDragAndDropFileToEditor: true,
  showXPathInStatusbar: false,
  buttons: [
    'source', '|',
    'bold', 'italic', 'underline', 'strikethrough', '|',
    'font', 'fontsize', 'brush', 'paragraph', '|',
    'ul', 'ol', '|',
    'table', 'link', '|',
    'left', 'center', 'right', 'justify', '|',
    'undo', 'redo', '|',
    'hr', 'eraser', 'fullsize'
  ],
  uploader: {
    insertImageAsBase64URI: true
  },
  colors: {
    background: '#222222',
    text: '#ffffff',
    defaultTextColor: '#ffffff',
    defaultBackgroundColor: '#222222'
  },
  style: {
    background: '#222222',
    color: '#ffffff'
  },
  statusbar: false,
  readonly: false,
  toolbarSticky: true,
  showPlaceholder: false,
  saveIndicator: false,
  maxHeight: 800,
  minHeight: 400,
  useSearch: false,
  spellcheck: false,
  editorCssClass: 'dark-theme',
}), []);
  

  const handleImageChange = (e, type) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      if (type === 'mobile') {
        setMobileImage(selectedFile);
        // Create and set the preview URL
        const previewUrl = URL.createObjectURL(selectedFile);
        setMobileImagePreview(previewUrl);
      } else {
        setComputerImage(selectedFile);
        // Create and set the preview URL
        const previewUrl = URL.createObjectURL(selectedFile);
        setComputerImagePreview(previewUrl);
      }
    }
  };

  // Clean up object URLs when component unmounts or when images change
  useEffect(() => {
    return () => {
      if (mobileImagePreview) {
        URL.revokeObjectURL(mobileImagePreview);
      }
      if (computerImagePreview) {
        URL.revokeObjectURL(computerImagePreview);
      }
    };
  }, [mobileImagePreview, computerImagePreview]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-');
  };

  const handleChapterChange = (index, field, value) => {
    const updatedChapters = [...ebookChapters];
    updatedChapters[index] = { ...updatedChapters[index], [field]: value };
    
    // Auto-generate slug when title changes
    if (field === 'title') {
      updatedChapters[index].slug = generateSlug(value);
    }
    
    setEbookChapters(updatedChapters);
  };

  const addChapter = () => {
    setEbookChapters([
      ...ebookChapters,
      {
        title: '',
        slug: '',
        description: '',
        full_content: '',
        chapter_number: ebookChapters.length + 1
      }
    ]);
  };

  const removeChapter = (index) => {
    if (ebookChapters.length > 1) {
      const updatedChapters = ebookChapters.filter((_, i) => i !== index);
      
      // Re-number remaining chapters
      updatedChapters.forEach((chapter, i) => {
        chapter.chapter_number = i + 1;
      });
      
      setEbookChapters(updatedChapters);
    }
  };

  const removeImage = (type) => {
    if (type === 'mobile') {
      setMobileImage(null);
      setMobileImagePreview(null);
      if (mobileImageInputRef.current) {
        mobileImageInputRef.current.value = '';
      }
    } else {
      setComputerImage(null);
      setComputerImagePreview(null);
      if (computerImageInputRef.current) {
        computerImageInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });

    try {
      if (ebookChapters.length === 0) {
        throw new Error('Please add at least one chapter');
      }

      if (formData.have_series && !formData.series_id) {
        throw new Error('Please select a series when adding chapter ebooks');
      }

      // Validate that all required fields are filled
      const missingTitleIndex = ebookChapters.findIndex(chapter => !chapter.title.trim());
      if (missingTitleIndex !== -1) {
        throw new Error(`Please provide a title for chapter #${missingTitleIndex + 1}`);
      }

      const missingContentIndex = ebookChapters.findIndex(chapter => !chapter.full_content.trim());
      if (missingContentIndex !== -1) {
        throw new Error(`Please provide content for chapter #${missingContentIndex + 1}`);
      }

      // Create form data for submission
      const submitFormData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        submitFormData.append(key, formData[key]);
      });

      // Add images if selected
      if (mobileImage) {
        submitFormData.append('mobileImage', mobileImage);
      }
      
      if (computerImage) {
        submitFormData.append('computerImage', computerImage);
      }

      // Add ebook metadata
      submitFormData.append('ebookData', JSON.stringify(ebookChapters));

      // Upload using the hook's mutation function
      uploadBatch(submitFormData, {
        onSuccess: (data) => {
          setMessage({ 
            text: `Success! ${data.ebooks.length} ebook chapters have been uploaded.`, 
            type: 'success' 
          });
          
          // Reset form
          setFormData({
            genre_id: '',
            language: '',
            author: '',
            is_public: false,
            have_series: false,
            series_id: '',
            author_id: profile?.id || '',
            format_type: 'ebook',
          });
          setEbookChapters([{
            title: '',
            slug: '',
            description: '',
            full_content: '',
            chapter_number: 1
          }]);
          setMobileImage(null);
          setMobileImagePreview(null);
          setComputerImage(null);
          setComputerImagePreview(null);
          
          // Reset file input elements
          if (mobileImageInputRef.current) mobileImageInputRef.current.value = '';
          if (computerImageInputRef.current) computerImageInputRef.current.value = '';
          
          // Refresh the ebook list
          refetchEbooks();
        }
      });
      
    } catch (error) {
      console.error('Error preparing ebook upload:', error);
      setMessage({ 
        text: error.message || 'Failed to prepare ebook files for upload', 
        type: 'error' 
      });
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Add Multiple Ebook Chapters</h1>
      
      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="genre_id">Genre</label>
          <select 
            id="genre_id" 
            name="genre_id" 
            value={formData.genre_id} 
            onChange={handleChange}
            required
            className={styles.select}
          >
            <option value="">Select Genre</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="language">Language</label>
          <select 
            id="language" 
            name="language" 
            value={formData.language} 
            onChange={handleChange}
            required
            className={styles.select}
          >
            <option value="">Select Language</option>
            <option value="english">English</option>
            <option value="spanish">Spanish</option>
            <option value="french">French</option>
            <option value="german">German</option>
            <option value="chinese">Chinese</option>
            <option value="japanese">Japanese</option>
            <option value="arabic">Arabic</option>
            <option value="hindi">Hindi</option>
            <option value="garhwali">Garhwali</option>
            <option value="kumaoni">Kumaoni</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="author">Author Name</label>
          <input
            type="text"
            id="author"
            name="author"
            value={formData.author}
            onChange={handleChange}
            required
            className={styles.input}
            placeholder="Enter author name"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input 
              type="checkbox" 
              name="is_public" 
              checked={formData.is_public} 
              onChange={handleChange}
              className={styles.checkbox}
            />
            Make Public
          </label>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input 
              type="checkbox" 
              name="have_series" 
              checked={formData.have_series} 
              onChange={handleChange}
              className={styles.checkbox}
            />
            Part of a Series
          </label>
        </div>

        {formData.have_series && (
          <div className={styles.formGroup}>
            <label htmlFor="series_id">Series</label>
            <select 
              id="series_id" 
              name="series_id" 
              value={formData.series_id} 
              onChange={handleChange}
              required={formData.have_series}
              className={styles.select}
            >
              <option value="">Select Series</option>
              {series.map(item => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </select>
          </div>
        )}
        {formData.have_series && selectedSeriesImage && (
          <div className={styles.seriesImagePreview}>
            <p className={styles.seriesImageLabel}>Series Cover Image Preview:</p>
            <img 
              src={selectedSeriesImage} 
              alt="Series cover preview" 
              className={styles.seriesImage} 
            />
          </div>
        )}

        <div className={styles.formGroup}>
          <label>Cover Images</label>
          <div className={styles.imageUploadContainer}>
            <div className={styles.imageUpload}>
              <label htmlFor="mobileImage">Mobile Cover Image</label>
              <input 
                type="file" 
                id="mobileImage" 
                name="mobileImage" 
                accept="image/*" 
                onChange={(e) => handleImageChange(e, 'mobile')}
                className={styles.fileInput}
                ref={mobileImageInputRef}
              />
              {mobileImage && (
                <div className={styles.imagePreview}>
                  <span>{mobileImage.name}</span>
                  {mobileImagePreview && (
                    <div className={styles.previewImageContainer}>
                      <img 
                        src={mobileImagePreview} 
                        alt="Mobile cover preview" 
                        className={styles.previewImage} 
                      />
                      <button 
                        type="button" 
                        onClick={() => removeImage('mobile')} 
                        className={styles.removeImageButton}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              )}

            </div>
            
            <div className={styles.imageUpload}>
              <label htmlFor="computerImage">Desktop Cover Image</label>
              <input 
                type="file" 
                id="computerImage" 
                name="computerImage" 
                accept="image/*" 
                onChange={(e) => handleImageChange(e, 'computer')}
                className={styles.fileInput}
                ref={computerImageInputRef}
              />
              {computerImage && (
                <div className={styles.imagePreview}>
                  <span>{computerImage.name}</span>
                  {computerImagePreview && (
                    <div className={styles.previewImageContainer}>
                      <img 
                        src={computerImagePreview} 
                        alt="Desktop cover preview" 
                        className={styles.previewImage} 
                      />
                      <button 
                        type="button" 
                        onClick={() => removeImage('computer')} 
                        className={styles.removeImageButton}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className={styles.chaptersSection}>
          <div className={styles.chaptersSectionHeader}>
            <h3>Ebook Chapters</h3>
            <button 
              type="button" 
              onClick={addChapter} 
              className={styles.addChapterButton}
            >
              Add Chapter
            </button>
          </div>
          
          {ebookChapters.map((chapter, index) => (
            <div key={index} className={styles.chapterItem}>
              <div className={styles.chapterHeader}>
                <h4>Chapter {chapter.chapter_number}</h4>
                {ebookChapters.length > 1 && (
                  <button 
                    type="button" 
                    onClick={() => removeChapter(index)} 
                    className={styles.removeChapterButton}
                  >
                    Remove
                  </button>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor={`title-${index}`}>Title</label>
                <input 
                  type="text" 
                  id={`title-${index}`} 
                  value={chapter.title} 
                  onChange={(e) => handleChapterChange(index, 'title', e.target.value)}
                  required
                  className={styles.input}
                  placeholder="Chapter title"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor={`slug-${index}`}>Slug (auto-generated)</label>
                <input 
                  type="text" 
                  id={`slug-${index}`} 
                  value={chapter.slug} 
                  onChange={(e) => handleChapterChange(index, 'slug', e.target.value)}
                  className={styles.input}
                  placeholder="chapter-slug"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor={`description-${index}`}>Description</label>
                <textarea 
                  id={`description-${index}`} 
                  value={chapter.description} 
                  onChange={(e) => handleChapterChange(index, 'description', e.target.value)}
                  className={styles.textarea}
                  placeholder="Brief description of this chapter"
                />
              </div>
              
               <div className={styles.formGroup}>
                <label htmlFor={`content-${index}`}>Content</label>
                <JoditEditor
                  ref={editor}
                  value={chapter.full_content}
                  config={joditConfig}
                  onChange={(newContent) =>
                    handleChapterChange(index, "full_content", newContent)
                  }
                />
              </div>
              
              {formData.have_series && (
                <div className={styles.formGroup}>
                  <label htmlFor={`chapter-number-${index}`}>Chapter Number</label>
                  <input 
                    type="number" 
                    id={`chapter-number-${index}`} 
                    value={chapter.chapter_number} 
                    onChange={(e) => handleChapterChange(index, 'chapter_number', Number(e.target.value))}
                    min="1"
                    required={formData.have_series}
                    className={styles.input}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className={styles.formAction}>
          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload Ebook Chapters'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddMultipleEbooks;